/**
 * Manager-facing live attendance triage.
 *
 * The endpoint already returns expected and actual times; this panel keeps
 * those semantics explicit instead of showing an unexplained bare "09:00".
 * Every exception is rendered in one consistent row so sparse categories do
 * not waste two-thirds of the page and large teams can scan one reading path.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import {
  ArrowForwardOutlined, CheckOutlined, CloseOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { meApi, type MeTeamExceptions } from '../../../api/me';
import { attendanceApi, ApiError } from '../../../api';
import { WidgetErrorState } from '../../../components/primitives';
import OverviewCard, { OverviewEmptyState } from './OverviewCard';
import { buildExceptionRows, type ExceptionKind, type ExceptionRowModel, type Translate } from './exceptionRows';
import { translateApiError } from '../../../i18n/translateApiError';

const KIND_COLOR: Record<ExceptionKind, string> = {
  notIn: 'error.main',
  late: 'warning.main',
  missingOut: '#C2185B',
  pending: 'info.main',
};

const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();

const CountChip: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Chip
    size="small"
    variant="outlined"
    label={`${value} ${label}`}
    sx={{
      height: 22,
      borderRadius: 1,
      borderColor: value > 0 ? color : 'divider',
      color: value > 0 ? color : 'text.secondary',
      fontSize: '0.68rem',
      fontWeight: value > 0 ? 700 : 500,
      bgcolor: 'background.paper',
    }}
  />
);

interface ExceptionRowProps {
  row: ExceptionRowModel;
  busy: boolean;
  onOpen: () => void;
  onDecide: (requestId: string, status: 'approved' | 'rejected') => void;
  t: Translate;
}

const ExceptionRow: React.FC<ExceptionRowProps> = ({ row, busy, onOpen, onDecide, t }) => (
  <Box
    sx={{
      minHeight: 74,
      display: 'grid',
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr) auto',
        md: 'minmax(180px, 1.35fr) minmax(120px, 0.75fr) minmax(155px, 1fr) minmax(180px, 1.2fr) auto',
      },
      alignItems: 'center',
      gap: { xs: 1, md: 2 },
      px: 1.25,
      py: 1,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      bgcolor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.secondary',
          fontSize: '0.68rem',
          fontWeight: 700,
        }}
      >
        {initials(row.name)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={row.name}>
          {row.name}
        </Typography>
        {row.department ? (
          <Typography variant="caption" color="text.secondary" noWrap title={row.department} sx={{ display: 'block' }}>
            {row.department}
          </Typography>
        ) : null}
      </Box>
    </Box>

    <Box sx={{ minWidth: 0, justifySelf: { xs: 'end', md: 'start' } }}>
      <Chip
        size="small"
        label={row.issue}
        sx={{
          height: 21,
          borderRadius: 1,
          color: KIND_COLOR[row.kind],
          bgcolor: 'action.hover',
          fontSize: '0.66rem',
          fontWeight: 700,
        }}
      />
      {row.issueDetail ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: KIND_COLOR[row.kind], fontSize: '0.66rem' }}>
          {row.issueDetail}
        </Typography>
      ) : null}
    </Box>

    <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / 2', md: 'auto' } }}>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {t('attendance:teamExceptions.expected')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
        {row.expected}
      </Typography>
    </Box>

    <Box sx={{ minWidth: 0, gridColumn: { xs: '2 / 3', md: 'auto' }, textAlign: { xs: 'right', md: 'left' } }}>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {t('attendance:teamExceptions.actual')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
        {row.actual}
      </Typography>
    </Box>

    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
      {row.requestId ? (
        <>
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={busy}
            startIcon={<CheckOutlined sx={{ fontSize: 14 }} />}
            onClick={() => onDecide(row.requestId!, 'approved')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.7rem', minWidth: 0, py: 0.25 }}
          >
            {t('attendance:teamExceptions.approve')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={busy}
            startIcon={<CloseOutlined sx={{ fontSize: 14 }} />}
            onClick={() => onDecide(row.requestId!, 'rejected')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.7rem', minWidth: 0, py: 0.25 }}
          >
            {t('attendance:teamExceptions.reject')}
          </Button>
        </>
      ) : (
        <Button
          size="small"
          variant="text"
          endIcon={<ArrowForwardOutlined sx={{ fontSize: 14 }} />}
          onClick={onOpen}
          sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.72rem', whiteSpace: 'nowrap' }}
        >
          {t('attendance:teamExceptions.viewEntries')}
        </Button>
      )}
    </Box>
  </Box>
);

const TodayExceptionsPanel: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<MeTeamExceptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await meApi.getTeamExceptions());
    } catch (e) {
      setData(null);
      setLoadError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:teamExceptions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const decide = useCallback(async (requestId: string, status: 'approved' | 'rejected') => {
    setBusyId(requestId);
    try {
      await attendanceApi.approvalsApi.update(requestId, { status });
      setData((prev) => prev ? {
        ...prev,
        pending_corrections: prev.pending_corrections.filter((p) => p.request_id !== requestId),
        counts: { ...prev.counts, pending_corrections: Math.max(0, prev.counts.pending_corrections - 1) },
      } : prev);
      setToast(t(status === 'approved' ? 'attendance:teamExceptions.approved' : 'attendance:teamExceptions.rejected'));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:teamExceptions.actionFailed'));
    } finally {
      setBusyId(null);
    }
  }, [t]);

  const approveAll = useCallback(async () => {
    const ids = (data?.pending_corrections ?? []).map((request) => request.request_id);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await attendanceApi.approvalsApi.bulkUpdate({ ids, status: 'approved' });
      setData((prev) => prev ? {
        ...prev,
        pending_corrections: [],
        counts: { ...prev.counts, pending_corrections: 0 },
      } : prev);
      setToast(t('attendance:teamExceptions.bulkApproved', { count: ids.length }));
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:teamExceptions.actionFailed'));
    } finally {
      setBulkBusy(false);
    }
  }, [data, t]);

  const rows = useMemo(() => data ? buildExceptionRows(data, t as Translate) : [], [data, t]);
  const counts = data?.counts;

  const summary = counts ? (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
      <CountChip label={t('attendance:teamExceptions.notIn')} value={counts.not_in} color="error.main" />
      <CountChip label={t('attendance:teamExceptions.late')} value={counts.late} color="warning.main" />
      <CountChip label={t('attendance:teamExceptions.missingOut')} value={counts.missing_out} color="#C2185B" />
      <CountChip label={t('attendance:teamExceptions.pending')} value={counts.pending_corrections} color="info.main" />
    </Stack>
  ) : null;

  return (
    <>
      <Box sx={{ width: '100%', mb: 2 }}>
        <OverviewCard
          title={t('attendance:teamExceptions.title')}
          subtitle={t('attendance:teamExceptions.subtitle')}
          action={summary}
          minHeight={250}
        >
          {loading ? (
            <Box sx={{ minHeight: 156, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : loadError ? (
            <WidgetErrorState error={loadError} onRetry={() => void load()} />
          ) : rows.length === 0 ? (
            <OverviewEmptyState
              title={t('attendance:teamExceptions.none')}
              hint={t('attendance:teamExceptions.noneHint')}
            />
          ) : (
            <Stack spacing={1}>
              {(data?.pending_corrections.length ?? 0) > 1 ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={bulkBusy}
                    startIcon={<CheckOutlined sx={{ fontSize: 14 }} />}
                    onClick={approveAll}
                    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.72rem', boxShadow: 'none' }}
                  >
                    {t('attendance:teamExceptions.approveAll', { count: data?.pending_corrections.length ?? 0 })}
                  </Button>
                </Box>
              ) : null}
              {rows.map((row) => (
                <ExceptionRow
                  key={row.key}
                  row={row}
                  busy={busyId === row.requestId}
                  onOpen={() => navigate(`/attendance/time-entries?worker=${encodeURIComponent(row.name)}`)}
                  onDecide={(requestId, status) => void decide(requestId, status)}
                  t={t as Translate}
                />
              ))}
            </Stack>
          )}
        </OverviewCard>
      </Box>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </>
  );
};

export default TodayExceptionsPanel;
